import { sem_imagem as semImg } from '@/constants';
import { useCallback, useRef, useState } from 'react';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { getOnStorage, removeFromStorage, setOnStorage } from '@/shared';
import { useClient, useFetchAxios, useGlobals, useUserContext } from '@/hooks';
import { Tables, toCoded, useDbFunctions } from '../dbFunctions/useDBFunctions';
import {
  tableKeys,
  TableTypes,
  CategoryProps,
  DataSetsProps,
  ProductProps,
  CustomerDataProps,
  EventsClientProps,
  PromocaoClientProps,
  DicUltimaCompraProps,
  CampanhaProdutosProps,
  PrecoPromocaoClienteProps,
} from '@/types';
import { useEvents } from '../use-events/useEvents';

type CatalogDictProps = Record<number, ProductProps>;
type CategoryDictProps = Record<number, CategoryProps>;

const MAX_CONCURRENT_DOWNLOADS = 2;
const CATALOG_CHUNK_SIZE = 400;
const CLIENTS_CHUNK_SIZE = 300;
const SET_DB_BATCH_SIZE = 5;
const PROGRESS_THROTTLE_MS = 150;
const SPEED_UPDATE_INTERVAL_MS = 500;

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], max: number): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;
  const runNext = async (): Promise<void> => {
    const i = index++;
    if (i >= tasks.length) return;
    try {
      const value = await tasks[i]();
      results[i] = { status: 'fulfilled', value };
    } catch (reason) {
      results[i] = { status: 'rejected', reason };
    }
    await runNext();
  };
  const workers = Array.from({ length: Math.min(max, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
};

const progressLastCall: Record<string, number> = {};
const updateProgress = (loaded: number, total: number, name: string) => {
  const now = Date.now();
  if (progressLastCall[name] != null && now - progressLastCall[name] < PROGRESS_THROTTLE_MS) return;
  progressLastCall[name] = now;

  const percentual = total > 0 ? Math.min((loaded / total) * 100, 100) : 0;
  const progressBar = document.getElementById(`${name}-bar`) as HTMLElement;
  const progressText = document.getElementById(`${name}-text`) as HTMLElement;
  const isValid = Number.isFinite(percentual);

  if (progressBar) progressBar.style.width = `${percentual}%`;
  if (progressText) progressText.textContent = isValid ? `${Math.round(percentual)}%` : 'Erro no Download';
};

export const useDownloadFiles = () => {
  const loading = useRef(false);
  const setDownloadSpeedBpsRef = useRef<(bps: number) => void>(() => {});
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hasError, setError] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileName = '';
  const [isOpenedModal, openModalSync] = useState(false);
  const [noUpdate, setNoUpdate] = useState(false as Boolean);
  const [datasets, setDataSets] = useState([] as DataSetsProps[]);

  
  const { axiosCtx } = useFetchAxios();
  const { registerEvent } = useEvents();
  const { setCustomer } = useUserContext();
  const { setOnDB, getOnDB } = useDbFunctions();
  const { setLastSync, getDate, setDownloadingFileName, setDownloadingFiles, downloadSpeedBps, setDownloadSpeedBps } = useGlobals();
  const { setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade } = useClient();
  
  setDownloadSpeedBpsRef.current = setDownloadSpeedBps;

  const handleDownloadingFileName = useCallback(() => {
    if (isSuccess || !isOpenedModal) {
      if (downloadLabelTimeoutRef.current) clearTimeout(downloadLabelTimeoutRef.current);
      return;
    }

    const listDataBase = getOnStorage('datasets');
    const ind = Math.floor(Math.random() * (listDataBase?.length || 0));
    let label = listDataBase?.[ind]?.DESC_ARQUIVO;
    if (label === fileName) {
      label = listDataBase?.[ind + 1]?.DESC_ARQUIVO || 'Baixando';
    }

    downloadLabelTimeoutRef.current = setTimeout(() => handleDownloadingFileName(), 3000);
  }, [fileName, isOpenedModal, isSuccess]);

  const fetchDatasets = useCallback(async (list?: DataSetsProps[]) => {
    var totalSize = 0;
    var downloadedSize = 0;
    var speedLastTime = Date.now();
    var speedLastBytes = 0;
    setDownloadSpeedBpsRef.current(0);

    const fetchAndSaveJson = async (url: string, name: string, desc: string): Promise<any> => {
      setDownloadingFileName(desc);
      registerEvent({
        tipoEvento: 'DOWNLOAD',
        obsEvento: `Iniciando download: ${name}`,
        codRca: Number(getOnStorage('cod_pessoa') || 0),
      })
      
      try {
        const resp = await fetch(url);
        if (resp.status >= 300 || resp.status < 200) {
          throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
        }

        const contentLength = Number(resp.headers.get('content-length')) || 0;
        totalSize += contentLength;
        let totalArchive = contentLength;
        let downloadedArchive = 0;

        const reader = resp?.body?.getReader();
        if (!reader) throw new Error('Failed to get response body');
        const decoder = new TextDecoder('utf-8');
        const chunks: string[] = [];

        while (true) {
          const result = await reader.read();
          const { done, value } = result;
          if (done) break;
          const len = value?.byteLength ?? 0;
          downloadedSize += len || 1;
          downloadedArchive += len || 1;
          const now = Date.now();
          if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
            const elapsed = (now - speedLastTime) / 1000;
            if (elapsed > 0) {
              const bps = (downloadedSize - speedLastBytes) / elapsed;
              setDownloadSpeedBpsRef.current(bps);
            }
            speedLastTime = now;
            speedLastBytes = downloadedSize;
          }
          updateProgress(downloadedArchive, totalArchive, name);
          updateProgress(downloadedSize, totalSize, 'progress');
          if (len) chunks.push(decoder.decode(value, { stream: true }));
        }
        progressLastCall[name] = 0;
        progressLastCall['progress'] = 0;
        updateProgress(downloadedArchive, totalArchive, name);
        updateProgress(downloadedSize, totalSize, 'progress');

        const json = JSON.parse(chunks.join(''));
        chunks.length = 0;
        let data = json?.catalogo ?? json?.preco ?? json;

        if (name === 'imagens') {
          data = typeof data === 'string' ? JSON.parse(data) : data;
          const out: Record<number, string> = {};
          if (Array.isArray(data)) {
            for (let j = 0; j < data.length; j++) {
              const item = data[j];
              out[item.COD_PRODUTO] = item.imagem;
            }
          }
          data = out;
        }
        if (typeof data === 'string') data = JSON.parse(data);
        registerEvent({
          tipoEvento: 'DOWNLOAD',
          obsEvento: `Download finalizado: ${name}`,
          codRca: Number(getOnStorage('cod_pessoa') || 0),
        })
        return { data, name };
      } catch (error) {
        console.error(`Error fetching ${name}:`, error);
        setError(true);
        registerEvent({
          tipoEvento: 'DOWNLOAD',
          obsEvento: `Falha no download: ${name}`,
          codRca: Number(getOnStorage('cod_pessoa') || 0),
          stEvento: 0,
        })
        throw error;
      }
    };

    const hasImagesOnDB = !!(await getOnDB('imagens'));
    const lastSyncImagesURL = getOnStorage('last_sync_images_url');
    
    const nonImagensList = list?.filter(({ TIPO_ARQUIVO }) => TIPO_ARQUIVO !== 'imagens') ?? [];
    const imagensItem = list?.find(({ TIPO_ARQUIVO }) => TIPO_ARQUIVO === 'imagens');
    
    const shouldDownloadImagens = imagensItem && (imagensItem.URL_S3 !== lastSyncImagesURL || !hasImagesOnDB);
    setDownloadingFiles([...nonImagensList, ...(shouldDownloadImagens ? [imagensItem] : [])]?.map((item) => item.DESC_ARQUIVO) ?? []);
    const tasksNonImagens = nonImagensList.map(({ TIPO_ARQUIVO, DESC_ARQUIVO, URL_S3 }) => () => fetchAndSaveJson(URL_S3, TIPO_ARQUIVO, DESC_ARQUIVO));
    const responseNonImagens = await runWithConcurrency(tasksNonImagens, MAX_CONCURRENT_DOWNLOADS);

    let fulfilled: Record<string, any> = {};
    const rejected: any[] = [];
    responseNonImagens.forEach((item) => {
      if (item.status === 'fulfilled') fulfilled[item.value.name] = item.value;
      if (item.status === 'rejected') rejected.push(item.reason);
    });

    if (shouldDownloadImagens) {
      setOnStorage('last_sync_images_url', imagensItem.URL_S3);
      try {
        const imgResult = await fetchAndSaveJson(imagensItem.URL_S3, 'imagens', imagensItem.DESC_ARQUIVO);
        fulfilled['imagens'] = imgResult;
      } catch (err) {
        rejected.push(err);
      }
    }

    if (rejected.length > 0) {
      console.warn("Promises rejeitadas:", rejected);
    }

    setDownloadSpeedBpsRef.current(0);
    return Object.keys(fulfilled).length ? fulfilled : undefined;
  }, [getOnDB]);

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[]) => {
      if (loading.current) return;

      loading.current = true;
      setError(false);
      setIsSuccess(false);
      updateProgress(0, 0, '.');

      // A lista de datasets a serem baixados é passada diretamente.
      const listToDownload = newDatasets || [];
      if (!listToDownload.length) {
        setNoUpdate(true);
        loading.current = false;
        return;
      }
      
      if (listToDownload.find(i => i.TIPO_ARQUIVO === 'estoque')) {
        removeFromStorage('stock_left');
      }

      const resp = await fetchDatasets(listToDownload);
      
      await registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Iniciando processamento de dados',
      })
      // Pega a lista antiga de datasets
      const oldDatasets = getOnStorage('datasets') || [];
      // Pega os datasets que foram baixados com sucesso
      const successfullyDownloaded = listToDownload.filter(d => resp && resp[d.TIPO_ARQUIVO]);
      
      // Cria um mapa da lista antiga para fácil substituição
      const oldDatasetsMap = oldDatasets.reduce((acc, item) => {
        acc[item.TIPO_ARQUIVO] = item;
        return acc;
      }, {} as Record<string, DataSetsProps>);

      // Atualiza o mapa com os arquivos baixados com sucesso
      successfullyDownloaded.forEach(item => {
        oldDatasetsMap[item.TIPO_ARQUIVO] = item;
      });

      // Converte o mapa de volta para uma lista e salva
      const finalDatasetList = Object.values(oldDatasetsMap);
      setOnStorage('datasets', finalDatasetList);
      setOnStorage('new_data_sets', finalDatasetList);
      setDataSets(listToDownload);

      setPreparing(true);

      const getData = async <T extends Tables>(dbKey: T, defaultValue: any): Promise<TableTypes[T]> => {
        if (resp?.[dbKey]?.data != null) return resp[dbKey].data as TableTypes[T];
        const fromDb = await getOnDB(dbKey);
        return (fromDb ?? defaultValue) as TableTypes[T];
      };

      const [
        precos,
        marcas,
        imagens,
        estoque,
        catalogo,
        saldoFlex,
        loadedClients,
        categorias,
        clienteBoletos,
        clienteEventosCortes,
        indicadores,
        campanha,
        listPageBuilder,
        janelaEntrega,
        parametrosAws,
        indicadorLoja,
        campanhaFaixa,
        campanhaProduto,
        campanhaCliente,
        campanhaTopMix,
        parametroPrecoBase,
        recomendacaoBigData,
        precoPromocaoCliente,
        cicloVendaCliente,
        percentualFornecedor,
        substituicaotributaria,
        restricaoProdutoCliente,
        ultimaCompra,
      ] = await Promise.all([
        getData('preco', []),
        getData('marcas', []),
        getData('imagens', {}),
        getData('estoque', []),
        getData('catalogo', []),
        getData('saldo_flex', []),
        getData('cliente', []),
        getData('categoria', []),
        getData('cliente_boletos', {}),
        getData('cliente_cortes', {}),
        getData('indicadores', []),
        getData('campanha_indicador', []),
        getData('pagebuilder', []),
        getData('janela_entrega', []),
        getData('parametros_aws', []),
        getData('indicador_loja', []),
        getData('campanha_faixa', []),
        getData('campanha_produto', {}),
        getData('campanha_cliente', {}),
        getData('campanha_mix_fornecedor', []),
        getData('parametro_preco_base', []),
        getData('recomendacaoBigData', []),
        getData('precoPromocaoCliente', []),
        getData('ciclo_venda_cliente_cliente', []),
        getData('percentual_fornecedor', []),
        getData('cod_regime_tributario', []),
        getData('restricaoProdutoCliente', []),
        getData('ultima_compra', []),
      ]);

      const yieldToMain = (): Promise<void> =>
        new Promise((resolve) => {
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => resolve(), { timeout: 50 });
          } else {
            setTimeout(resolve, 0);
          }
        });

      const eventos: EventsClientProps = clienteBoletos?.clientes?.reduce((acc, cliente) => {
        return {
          ...acc,
         [cliente.cod_cliente]: {
           boletos: cliente.boletos,
          cod_cliente: cliente.cod_cliente,
         }
        }
      }, {} as EventsClientProps) || {};

      await yieldToMain();

      const objCategory = {} as CategoryDictProps;

      const parseJSON = ((item: any) => {
        try {
          return JSON.parse(item)
        } catch (error) {
          return item
        }
      })

      const cycleByClient: Record<number, typeof cicloVendaCliente> = {};
      if (Array.isArray(cicloVendaCliente)) {
        for (let j = 0; j < cicloVendaCliente.length; j++) {
          const cycle = cicloVendaCliente[j];
          const cod = cycle.COD_CLIENTE;
          if (!cycleByClient[cod]) cycleByClient[cod] = [];
          cycleByClient[cod].push(cycle);
        }
        Object.keys(cycleByClient).forEach((cod) => {
          cycleByClient[Number(cod)].sort((a, b) => a.SEMANA_CICLO_ID - b.SEMANA_CICLO_ID);
        });
      }

      const sortedClients = loadedClients?.sort((a, b) => {
        if (a.NM_FANTASIA > b.NM_FANTASIA) return 1;
        if (a.NM_FANTASIA < b.NM_FANTASIA) return -1;
        return 0;
      }) || [];
      const clientes: (typeof sortedClients[0] & { CYCLE: typeof cicloVendaCliente })[] = [];
      for (let i = 0; i < sortedClients.length; i += CLIENTS_CHUNK_SIZE) {
        const chunk = sortedClients.slice(i, i + CLIENTS_CHUNK_SIZE);
        chunk.forEach((c) => {
          c.COD_SEGMENTO = parseJSON(c?.COD_SEGMENTO || '[]');
          c.DT_ULTIMA_COMPRA = parseJSON(c?.DT_ULTIMA_COMPRA || '[]');
          c.UNILEVER = c.FL_UNILEVER === 'True';
          const CYCLE = cycleByClient[c.COD_CLIENTE] || [];
          clientes.push({ ...c, CYCLE });
        });
        await yieldToMain();
      }

      await yieldToMain();

      const mapedPrices = precos.reduce((acc: any, i: any) => {
        if (!acc[i.COD_PRODUTO]) {
          acc[i.COD_PRODUTO] = []
        }
        acc[i.COD_PRODUTO].push(i)
        return acc
      }, {})

      const promocaoCliente = Array.isArray(precoPromocaoCliente) ? precoPromocaoCliente.reduce((acc: PrecoPromocaoClienteProps, p: PromocaoClientProps) => {
        const clientPrice = acc[p.COD_CLIENTE]
        
        if (!clientPrice) {
          acc[p.COD_CLIENTE] = { [p.QTD_EMBALAGEM]: { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } } }
          return acc
        }
        

        const qtdEmbalagem = clientPrice[p.QTD_EMBALAGEM]
        if (!qtdEmbalagem) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM] = { [p.COD_PRODUTO]: { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } } }
          return acc
        }
        

        const productPrice = qtdEmbalagem[p.COD_PRODUTO]
        if (!productPrice) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO] = { [p.COD_LOJA]: { [p.NRO_SEGMENTO]: p } }
          return acc
        }
        

        const productPriceLoja = productPrice[p.COD_LOJA]
        if (!productPriceLoja) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA] = { [p.NRO_SEGMENTO]: p }
          return acc
        }
        

        const productPriceSegmento = productPriceLoja[p.NRO_SEGMENTO]
        if (!productPriceSegmento) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p
          return acc
        }

        
        if (p.VLR_PRECO < productPriceSegmento.VLR_PRECO) {
          acc[p.COD_CLIENTE][p.QTD_EMBALAGEM][p.COD_PRODUTO][p.COD_LOJA][p.NRO_SEGMENTO] = p
          return acc
        }

        return acc
      }, {}) : precoPromocaoCliente;

      await yieldToMain();

      const productUltimaCompra: DicUltimaCompraProps = {};
      if (Array.isArray(ultimaCompra)) {
        for (let j = 0; j < ultimaCompra.length; j++) {
          const c = ultimaCompra[j];
          productUltimaCompra[c.SEQPRODUTO] = c;
        }
      }

      let products: ProductProps[] = [];
      const objProduct = {} as CatalogDictProps;
      for (let i = 0; i < catalogo.length; i += CATALOG_CHUNK_SIZE) {
        const chunk = catalogo.slice(i, i + CATALOG_CHUNK_SIZE);
        chunk.forEach((item: ProductProps) => {
          const PRECO = mapedPrices[item.COD_PRODUTO];
          if (!PRECO?.length) return;

          const EMBALAGEM = item?.EMBALAGEM?.map((emb) => {
            if (emb?.QTD_MULTIPLO_VENDA > 1) {
              return { ...emb, SG_EMBALAGEM: "PK" }
            }
            return { ...emb }
          });
          const IMAGEM = imagens?.[item?.COD_PRODUTO] || imagens['sem_imagem'] || semImg;

          const product = {
            ...item,
            PRECO,
            IMAGEM,
            EMBALAGEM,
            UNILEVER: item.FL_UNILEVER === 'True',
            ultimaCompra: productUltimaCompra[item.COD_PRODUTO]
          } as ProductProps;

          products.push(product);
          objProduct[item.COD_PRODUTO] = product;
        });
        await yieldToMain();
      }

      await yieldToMain();

      if (!!categorias?.length) {
        categorias?.forEach((item: CategoryProps) => {
          if (!objCategory[item?.COD_CATEGORIA]) {
            objCategory[item?.COD_CATEGORIA] = {
              ...item,
            };
          }
        });
      }

      const mountCategories = (campanha: CampanhaProdutosProps) => {
        if (!campanha) return []
        
        return [{
          NRO_HIERARQUIA: 1,
          DE_CATEGORIA: campanha.nome,
          IMAGE: campanha?.inativo?.selo || '',
          PATH_CATEGORIA: campanha.url_amigavel,
        }] as CategoryProps[]
      }

      const isLoaded = !!categorias.find(c => {
        return c.PATH_CATEGORIA === campanhaProduto?.url_amigavel
      })
      const allCategories = [...(isLoaded ? [] : mountCategories(campanhaProduto)), ...categorias]
      const pageBuilder = listPageBuilder.map((i: any, ind: any) => ({ ...i, ID: toCoded(`${ind + 1}`, true, true) }))

      await yieldToMain();

      const dbWrites: [data: any, key: Tables][] = [
        [marcas, 'marcas'],
        [precos, 'preco'],
        [estoque, 'estoque'],
        [imagens, 'imagens'],
        [catalogo, 'catalogo'],
        [clientes, 'cliente'],
        [products, 'produtos'],
        [saldoFlex, 'saldo_flex'],
        [pageBuilder, 'pagebuilder'],
        [indicadores, 'indicadores'],
        [allCategories, 'categoria'],
        [campanha, 'campanha_indicador'],
        [parametrosAws, 'parametros_aws'],
        [janelaEntrega, 'janela_entrega'],
        [campanhaFaixa, 'campanha_faixa'],
        [indicadorLoja, 'indicador_loja'],
        [objProduct, 'dicionario_produtos'],
        [eventos, 'eventos'],
        [campanhaCliente, 'campanha_cliente'],
        [campanhaProduto, 'campanha_produto'],
        [objCategory, 'dicionario_categorias'],
        [promocaoCliente, 'precoPromocaoCliente'],
        [campanhaTopMix, 'campanha_mix_fornecedor'],
        [parametroPrecoBase, 'parametro_preco_base'],
        [recomendacaoBigData, 'recomendacaoBigData'],
        [percentualFornecedor, 'percentual_fornecedor'],
        [substituicaotributaria, 'cod_regime_tributario'],
        [cicloVendaCliente, 'ciclo_venda_cliente_cliente'],
        [restricaoProdutoCliente, 'restricaoProdutoCliente'],
        [ultimaCompra, 'ultima_compra'],
        [clienteEventosCortes, 'cliente_cortes'],
        [clienteBoletos, 'cliente_boletos'],
      ];
      
      for (let b = 0; b < dbWrites.length; b += SET_DB_BATCH_SIZE) {
        const batch = dbWrites.slice(b, b + SET_DB_BATCH_SIZE);
        await Promise.all(batch.map(([data, key]) => setOnDB(data, key)));
        await yieldToMain();
      }
      
      await registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: 'Processamento de dados finalizado',
      })
      
      if (downloadLabelTimeoutRef.current) {
        clearTimeout(downloadLabelTimeoutRef.current);
        downloadLabelTimeoutRef.current = null;
      }
      const rcaCustomer = getOnStorage('rca_usuario');
      
      setCustomer(rcaCustomer as CustomerDataProps);
      setCampanhaProdutos(campanhaProduto || {});
      setCampanhaClientes(campanhaCliente || {});
      setCampanhaFaixa(campanhaFaixa || []);
      setCampanhaTopMix(campanhaTopMix || []);
      setIndicadorLoja(indicadorLoja || []);
      setIndicadoresProdutividade(indicadores || []);
      
      setPreparing(false);
      setIsSuccess(true);
      loading.current = false;
      
      return resp
    },
    [fetchDatasets, getOnDB, setDownloadingFileName, setOnDB, setCustomer, setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade]
  );

  const GETDATASET_TIMEOUT_MS = 45000;
  const GETDATASET_STOCK_TIMEOUT_MS = 15000;

  const updateStock = useCallback(async () => {
    try {
      const rcaInfos = getOnStorage('rca_usuario');
      if (!rcaInfos?.codPessoa) return;

      const resp: any = await axiosCtx
        .get(`/getdataset/${rcaInfos.codPessoa}`, { timeout: GETDATASET_STOCK_TIMEOUT_MS });
      const [representante] = resp?.data?.representante || [];
      const newList: DataSetsProps[] = representante?.link_Dataset || [];

      if (!newList.length) return;

      const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
      const newStockFile = newList.find(item => item.TIPO_ARQUIVO === 'estoque');
      const oldStockFile = oldList.find(item => item.TIPO_ARQUIVO === 'estoque');

      if (newStockFile && newStockFile.URL_S3 !== oldStockFile?.URL_S3) {
        const stockResponse = await fetchDatasets([newStockFile]);
        
        if (stockResponse?.estoque?.data) {
          await setOnDB(stockResponse.estoque.data, 'estoque');
          const updatedOldList = oldList.filter(item => item.TIPO_ARQUIVO !== 'estoque');
          const updatedDataSet = [...updatedOldList, newStockFile];
          
          setOnStorage('datasets', updatedDataSet);
          setOnStorage('stock_left', {});
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar o estoque:", error);
      setError(true);
    }
  }, [axiosCtx, fetchDatasets, setOnDB]);
  
  const getLinks = useCallback(async ({isBackground}: {isBackground?: boolean} = {isBackground: false}) => {
    setPreparing(true);
    setError(false);
    setIsSuccess(false);

    const rcaInfos = getOnStorage('rca_usuario');
    const codPessoa = rcaInfos?.codPessoa ?? rcaInfos?.cod_pessoa;
    if (!codPessoa) {
      setPreparing(false);
      setError(true);
      return;
    }

    if (!isBackground) {
      try { localStorage.setItem('login_bootstrap', 'in-progress'); } catch {}
    }

    const resp: any = await axiosCtx
      .get(`/getdataset/${codPessoa}`, { timeout: GETDATASET_TIMEOUT_MS })
      .catch(() => 'error');
    
    if (resp === 'error') {
      setPreparing(false);
      setError(true);
      return;
    }
    
    const [representante] = resp?.data?.representante || [];
    const list = representante?.link_Dataset || [];

    const oldList: DataSetsProps[] = getOnStorage('datasets') || [];
    const urlSet = new Set(oldList.map((i) => i.URL_S3));
    let filteredList = list.filter((dt: DataSetsProps) => !urlSet.has(dt.URL_S3));

    const CRITICAL_TABLES: Tables[] = tableKeys as unknown as Tables[];
    if (filteredList.length === 0 && oldList.length > 0) {
      const listByType = list.reduce((acc: Record<string, DataSetsProps>, i: DataSetsProps) => {
        acc[i.TIPO_ARQUIVO] = i;
        return acc;
      }, {});
      const toCheck = oldList.filter((el) =>
        tableKeys.includes(el.TIPO_ARQUIVO as Tables) && CRITICAL_TABLES.includes(el.TIPO_ARQUIVO as Tables)
      );

      const emptyTables = await Promise.all(
        toCheck.map(async (element) => {
          const item = await getOnDB(element.TIPO_ARQUIVO as Tables);
          const isFill = Array.isArray(item) ? !!item?.length : !!Object.keys(item || {}).length;
          return isFill ? null : listByType[element.TIPO_ARQUIVO] ?? null;
        })
      );
      const toAdd = emptyTables.filter((el): el is DataSetsProps => el != null);
      filteredList = [...filteredList, ...toAdd];
    }

    const data = await loadFiles(filteredList);
    
    const formattedDate = getDate();
    setLastSync(formattedDate);
    // Concluiu com sucesso o bootstrap de login (download dos datasets necessários)
    if (!isBackground) {
      try { localStorage.setItem('login_bootstrap', 'done'); } catch {}
    }
    
    if(isBackground) return filteredList;

    setTimeout(() => {
      unregisterServiceWorker({preserveLocalStorage: isBackground});
    }, 100);
    return data
  }, [loadFiles, setLastSync, getDate, axiosCtx]);

  return {
    loading: loading.current,
    hasError,
    noUpdate,
    fileName,
    datasets,
    preparing,
    isSuccess,
    isOpenedModal,
    downloadSpeedBps,

    setError,
    loadFiles,
    getLinks,
    updateStock,
    openModalSync,
  };
};
